import {
  Component,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  SimpleChanges,
} from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { LoggingService } from "@core/services";
import { LookupListService } from "@modules/common/services/lookup-list.service";
import {
  NgbActiveModal,
  NgbCalendar,
  NgbDate,
  NgbModalRef,
} from "@ng-bootstrap/ng-bootstrap";
import {
  allocPercent,
  BaseCalendarDto,
  fullName,
  JobDto,
  ListResultDto,
  newListResult,
  rating,
  ResourceScheduleDto,
  ScheduleJobDto,
} from "@shared/models";
import {
  addWorkingDays,
  convertToDate,
  convertToNgbDate,
  getValueFromPath,
} from "../../common";
import { TableColumn } from "../../common/components/index";
import {
  DealerService,
  JobService,
  ProductSettingService,
  ResourceService,
} from "../../common/services";

@Component({
  selector: "abi-schedule-job-modal",
  templateUrl: "./schedule-job.component.html",
  styleUrls: ["./schedule-job.component.scss"],
})
export class ScheduleJobComponent implements OnInit, OnChanges {
  jobAtBranch = false;
  pageNumber = 1;
  pageSize = 20;
  pageSizes = [20, 50, 100];

  readonly minDate = convertToNgbDate(Date.today());
  bookedDate = null;
  date: Date;
  @Input() job: JobDto;
  resources: ListResultDto<ResourceScheduleDto> = null;
  masterResources: ListResultDto<ResourceScheduleDto> =
    newListResult<ResourceScheduleDto>();
  reason: string;
  resourceId: string;
  appointments: { date: Date; count: number }[] = [];
  allResources = false;
  serviceTimeSlots: Map<number, { id: string; available: number }> = new Map();
  scheduleCalendar: BaseCalendarDto;
  isDayDisabled: (date: NgbDate, current: { month: number }) => boolean; // directly used by the calendar component

  columns: TableColumn[] = [
    {
      field: "resourceId",
      header: "JobSchedule.ID",
      tdClass: "bg-white p-1",
      skipFilter: false,
      class: "w-10",
    },
    {
      field: "",
      header: "Contact.Name",
      class: "d-md-table-cell w-15",
      skipFilter: true,
    },
    {
      field: "resourceZoneId",
      header: "JobSchedule.Zone",
      class: "text-center w-5",
    },
    {
      field: "maximum",
      header: "JobSchedule.Max",
      class: "text-right w-5",
    },
    {
      field: "allocated",
      header: "JobSchedule.Alloc",
      class: "text-right w-5",
    },
    // { field: "allowPercent", header: "%", class: "text-right" },
    {
      field: "jobType",
      header: "JobSchedule.Type",
      class: "text-right w-5",
    },
    {
      field: "postalCode",
      header: "JobSchedule.Postal",
      class: "text-right w-5",
    },
    {
      field: "skills",
      header: "JobSchedule.Skill",
      class: "text-right w-5",
    },
  ];
  slotModel: number;
  loading = false;

  constructor(
    private jobService: JobService,
    private resourceService: ResourceService,
    private productSettings: ProductSettingService,
    private lookups: LookupListService,
    private dealerService: DealerService,
    private calendarService: NgbCalendar,
    private loggingService: LoggingService,
    @Optional() public dialogRef: MatDialogRef<any>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: JobDto,
    @Optional() public modalRef?: NgbActiveModal
  ) {
    this.jobAtBranch = productSettings.booleanValue("JobAtBranch");
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.job && changes.job.currentValue) {
      this.setup(changes.job.currentValue);
    }
  }

  ngOnInit(): void {
    if (this.dialogRef) this.dialogRef.updateSize("768px");
    // if(this.data) {
    this.setup(this.data || this.job);
    // }
  }

  setup(data: JobDto) {
    this.job = data;
    let date = this.job.booked;
    this.bookedDate = new Date(date).valueOf();
    this.slotModel = this.bookedDate; // default slot selection (booked date - includes date AND TIME)

    const includeWeekend =
      this.productSettings.booleanValue("JobIncludesWeekend");
    if (!date || date < Date.today()) {
      date = addWorkingDays(Date.today(), 1, includeWeekend ? [] : [0, 6]);
    }
    this.loadAppointmentDate(date);
  }

  selectDate(date: Date) {
    this.loadAppointmentDate(date);
  }

  selectSlot(unixDate: number) {
    this.date = new Date(unixDate);
  }

  resourceResultSetLength = 0;
  get resourceListLength() {
    if (this.allResources) return this.resourceResultSetLength;
    return this.resources.items.length;
  }
  showAllResources() {
    this.allResources = true;
    this.resources = this.masterResources;
    this.resourceResultSetLength = this.resources.items.length;
  }

  private getAppointments(): void {
    if (this.resourceId) {
      this.resourceService
        .getAppointments(this.resourceId, this.date)
        .toPromise()
        .then((app) => {
          this.appointments = Object.keys(app).map((dt) => ({
            date: new Date(dt + "T00:00:00"),
            count: app[dt],
          }));
          this.loading = false;
        });
    } else {
      this.loading = false;
    }
  }

  /**
   * A more general function to check for format settings before displaying a value
   */
  resourceDisplaySetting(
    columnId: string,
    row: ResourceScheduleDto,
    defaultValue: string = ""
  ): string {
    const format = this.productSettings.stringValue(
      "ScheduleResourceDisplayFormat-" + columnId
    ); // || '{contact.title} {contact.firstName} {contact.lastName}';
    const resource = this.resourceService.selectResource(row.resourceId);
    if (format && resource) return getValueFromPath(resource, format).trim();
    return defaultValue;
  }

  /**
   * Find display form setting or use the Fullname of the Contact
   */
  resourceName(resourceId: string): string {
    const format = this.productSettings.stringValue(
      "ScheduleResourceDisplayFormat-name"
    ); // || '{contact.companyName}';
    const resource = this.resourceService.selectResource(resourceId);
    if (format && resource) return getValueFromPath(resource, format).trim();
    return resource ? fullName(resource.contact) : "-";
  }

  changeResource(dto: ResourceScheduleDto): void {
    this.resourceId = dto.resourceId;
    this.getAppointments();
  }

  resourceTdStyle(dto: ResourceScheduleDto): string {
    if (rating(dto) === 0) {
      return "danger";
    }
    if (allocPercent(dto) >= 100) {
      return "warning";
    }
    return "success";
  }

  resourceTrStyle(dto: ResourceScheduleDto): string {
    if (rating(dto) === 0) {
      return "table-light-danger";
    }
    if (allocPercent(dto) >= 100) {
      return "table-light-warning";
    }
    return "table-light-success";
  }

  blank(value: number): string {
    return value ? value.toString() : "";
  }

  onDateChange(dateStruct: NgbDate) {
    this.loadAppointmentDate(convertToDate(dateStruct));
  }

  loadAppointmentDate(date: Date) {
    if (date.valueOf() !== this.date?.valueOf()) {
      this.resources = null;
      this.date = date;

      this.loading = true;
      this.jobService
        .getScheduleJob(this.job.jobId, new Date(this.date))
        .toPromise()
        .then((resourceSchedules) => {
          this.appointments = [];
          this.serviceTimeSlots = new Map();

          // No Resources may be available for the current date - just end (showing default 'empty' message)
          if (!resourceSchedules.length) {
            // No resources availanble for ucrrent date
            this.loading = false;
            return;
          }
          const resourceResultList =
            newListResult<ResourceScheduleDto>(resourceSchedules);
          this.masterResources = resourceResultList; // all available Resources
          // this.masterResources = resourceSchedules;// all available Resources
          // Auto-select the system's applicable resources
          this.resources = newListResult(
            this.masterResources.items.filter(
              (f) => rating(f) > 0 || f.resourceId === this.job.resourceId
            )
          );
          this.allResources =
            this.masterResources.items.length === this.resources.items.length;

          // auto select 'green' resource
          if (!this.resourceId) {
            if (this.job.resourceId) {
              this.resourceId = this.job.resourceId;
            } else {
              const green = this.resources.items.find(
                (r) => r.allocated < 100 && rating(r) > 0
              );
              if (green) {
                this.resourceId = green.resourceId;
              }
            }
          }

          // job had a resource assigned, but schedule info does not have that resource (then unset current resource)
          if (
            this.resourceId &&
            !this.resources.items.some((r) => r.resourceId === this.resourceId)
          ) {
            this.resourceId = "";
          }

          // Time-Slots or Appointments
          if (this.jobAtBranch) {
            this.getSlots();
          } else {
            this.getAppointments();
          }
        });
    }
  }

  getSlots() {
    // The Job's Visits would have at least 1 visit entry, we use the DealerBranchId to get the Slots
    const visitBooking = this.job.visits.find((item) => item.dealerBranchId);
    if (!visitBooking) {
      this.loggingService.logException(
        new Error(
          `CRITICAL: Unable to find a Visit in Job that has a DealerBranch - cannot continue scheduling`
        )
      );
      return;
    }
    this.dealerService
      .queryBranches(visitBooking.dealerBranchId)
      .subscribe((dataList) => {
        if (dataList && dataList.length) {
          // @ts-ignore 'baseCalendarID' is not a valid DTO variable
          const baseCalendarId = dataList[0].baseCalendarID;
          this.lookups
            .lookupListEx<BaseCalendarDto>("CodeBaseCalendar")
            .subscribe((llCals) => {
              this.scheduleCalendar = llCals.item(baseCalendarId);
              // set the 'isDayDisabled' prop here as the Calendar will trigger a repaint due to binding
              this.isDayDisabled = (date: NgbDate): boolean => {
                if (!this.scheduleCalendar) return false;
                const currentWeekDay = this.calendarService.getWeekday(date);
                return !this.scheduleCalendar.workDays.some(
                  (workDay) => workDay.dayOfWeek === currentWeekDay
                );
              };
            });
        }
      });

    this.jobService
      .getAvailableTimeSlotsByResource(
        this.resourceId,
        new Date(this.date).format("yyyy-MM-dd")
      )
      .subscribe(
        (timeSlots: { [key: string]: { id: string; available: number } }) => {
          this.loading = false;
          this.serviceTimeSlots = new Map(
            Object.entries(timeSlots).map((entry) => {
              // ensure dates are generated freshly to prevent differences between server and client date formatting
              return [new Date(entry[0]).valueOf(), entry[1]];
            })
          );
        }
      );
  }

  setPageSize(size: number) {
    localStorage.setItem("pageSize", size.toString());
    this.pageSize = size;
    // this.search(this.columns, true);
  }

  pagedResources(): ResourceScheduleDto[] {
    const start = (this.pageNumber - 1) * this.pageSize;
    return this.resources.items
      ? this.resources.items.slice(start, start + this.pageSize)
      : [];
  }

  submitting = false;
  accept() {
    this.submitting = true;
    const dto: ScheduleJobDto = {
      jobId: this.job.jobId,
      date: this.date,
      resourceId: this.resourceId,
      reason: this.reason,
    };
    this.jobService
      .scheduleJob(dto)
      .toPromise()
      .then((job) => {
        this.closeModal(job);
      })
      .finally(() => {
        this.submitting = false;
      });
  }

  cancel() {
    this.closeModal();
  }

  closeModal(job?: JobDto) {
    if (this.dialogRef) this.dialogRef.close(job);
    else if (this.modalRef) this.modalRef.close(job);
  }

  filteredResources(rows: any) {
    this.resourceResultSetLength = rows.length;
  }
}
